import React, { useState } from 'react';
import { Paper, Typography, List, Collapse, IconButton, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GameItem from './GameItem';
import { GroupedGames, DayData, LotteryEntry } from '../types/types';

type ReservationMode = 'LOTTERY' | 'FCFS';

interface GameListProps {
  games: GroupedGames;
  days: { [date: string]: DayData };
  loadingButtons: { [gameId: number]: boolean };
  bookingPeriodOpen: boolean
  tokenMap: { [gameId: number]: string };
  onReserve: (gameId: number, day: string) => void;
  onCancelInitial: (gameId: number) => void;
  onCancelConfirmed: (gameId: number) => void;
  onSetWinner?: (entryId: string) => void;
  onCancelLotteryEntry?: (entryId: string) => void;
  onToggleDisableGame?: (gameId: number, disabled: boolean) => void;
  mode?: ReservationMode;
  lotteryEntries?: Record<number, LotteryEntry[]>;
  disabledGameIds?: Set<number>;
  isAdmin?: boolean;
}

const GameList: React.FC<GameListProps> = ({
  games,
  days,
  loadingButtons,
  bookingPeriodOpen,
  tokenMap,
  onReserve,
  onCancelInitial,
  onCancelConfirmed,
  onSetWinner,
  onCancelLotteryEntry,
  onToggleDisableGame,
  mode = 'FCFS',
  lotteryEntries,
  disabledGameIds,
  isAdmin = false,
}) => {
  const [visibilityMap, setVisibilityMap] = useState<{ [date: string]: boolean }>({});

  const toggleVisibility = (date: string) => {
    setVisibilityMap((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  return (
    <>
      {Object.keys(games).length === 0 ? (
        <Typography></Typography>
      ) : (
        Object.entries(games).map(([date, gamesList]) => {
          const sortedGames = [...gamesList].sort((a, b) => {
            const aIsReserved =
              days[date]?.status === 'RESERVED' && days[date]?.game_id === a.id;
            const bIsReserved =
              days[date]?.status === 'RESERVED' && days[date]?.game_id === b.id;
            return (bIsReserved ? 1 : 0) - (aIsReserved ? 1 : 0);
          });

          const reservedGame = sortedGames.find(
            (game) => days[date]?.status === 'RESERVED' && days[date]?.game_id === game.id
          );

          const otherGames = sortedGames.filter(
            (game) => !(days[date]?.status === 'RESERVED' && days[date]?.game_id === game.id)
          );

          const disableButtons = sortedGames.some((game) => loadingButtons[game.id]);

          const isPending = days[date] && days[date].status === 'PENDING';

          const isOwnPending = isPending && sortedGames.some(g => !!tokenMap[g.id]);

          const formattedDate = 
          new Date(date).toLocaleDateString("fi-FI", {
            weekday: "short"
          }) +
          " " + 
          new Date(date).toLocaleDateString("fi-FI", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });

          const combinedLotteryEntries = lotteryEntries ? Object.values(lotteryEntries).flat() : [];
          const lotteryWinner = combinedLotteryEntries.find(
            (l: LotteryEntry) => l.day === date && l.status === 'WINNER'
          );
          return (
            <Paper key={date} style={{ padding: '16px 16px 0px 16px ', marginBottom: '16px' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              {formattedDate} 
              {isPending && ` (Alustava varaus)`}
              {days[date] && days[date].status === 'RESERVED' && ' (Varattu)'}
              {lotteryWinner &&  ' ' + lotteryWinner.person_name + ' (Arvottu)'}
            </Typography>

              <List>
                {reservedGame && (
                  <GameItem
                    key={reservedGame.id}
                    game={reservedGame}
                    loading={loadingButtons[reservedGame.id]}
                    disableButtons={true}
                    isReserved={true}
                    isPending={false}
                    disableReserve={true}
                    tokenFound={!!tokenMap[reservedGame.id]}
                    publicName={days[date]?.public_name}
                    onReserve={onReserve}
                    onCancelInitial={onCancelInitial}
                    onCancelConfirmed={onCancelConfirmed}
                    onSetWinner={onSetWinner}
                    onCancelLotteryEntry={onCancelLotteryEntry}
                    onToggleDisableGame={onToggleDisableGame}
                    bookingPeriodOpen={bookingPeriodOpen}
                    isOwnPending={isOwnPending}
                    isAdmin={isAdmin}
                    mode={mode}
                    lotteryEntries={lotteryEntries ? lotteryEntries[reservedGame.id] : undefined}
                    isGameDisabled={disabledGameIds?.has(reservedGame.id) ?? false}
                  />
                )}

                {days[date]?.status === 'RESERVED' && otherGames.length > 0 && (
                  <>
                    <Box display="flex" justifyContent="center" alignItems="center" marginTop="8px">
                      <IconButton
                        size="small"
                        onClick={() => toggleVisibility(date)}
                        style={{ fontSize: '0.8rem' }}
                      >
                        {visibilityMap[date] ? (
                          <>
                            <ExpandLessIcon fontSize="small" /> Piilota muut päivän ottelut
                          </>
                        ) : (
                          <>
                            <ExpandMoreIcon fontSize="small" /> Näytä muut päivän ottelut (ei varattavissa)
                          </>
                        )}
                      </IconButton>
                    </Box>
                    <Collapse in={visibilityMap[date]} timeout="auto" unmountOnExit>
                      <List>
                        {otherGames.map((game) => (
                          <GameItem
                            key={game.id}
                            game={game}
                            loading={loadingButtons[game.id]}
                            disableButtons={true}
                            isReserved={false}
                            isPending={false}
                            disableReserve={true}
                            tokenFound={!!tokenMap[game.id]}
                            onReserve={onReserve}
                            onCancelInitial={onCancelInitial}
                            onCancelConfirmed={onCancelConfirmed}
                            onSetWinner={onSetWinner}
                            onCancelLotteryEntry={onCancelLotteryEntry}
                            onToggleDisableGame={onToggleDisableGame}
                            bookingPeriodOpen={bookingPeriodOpen}
                            isOwnPending={isOwnPending}
                            isAdmin={isAdmin}
                            mode={mode}
                            lotteryEntries={lotteryEntries ? lotteryEntries[game.id] : undefined}
                            isGameDisabled={disabledGameIds?.has(game.id) ?? false}
                          />
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}

                {days[date]?.status !== 'RESERVED' &&
                  sortedGames.map((game) => (
                    <GameItem
                      key={game.id}
                      game={game}
                      loading={loadingButtons[game.id]}
                      disableButtons={!bookingPeriodOpen || disableButtons || days[date]?.status === 'PENDING'}
                      isReserved={false}
                      isPending={isPending}
                      isAdmin={isAdmin}
                      isOwnPending={isOwnPending}
                      disableReserve={false}
                      tokenFound={!!tokenMap[game.id]}
                      bookingPeriodOpen={bookingPeriodOpen}
                      onReserve={onReserve}
                      onCancelInitial={onCancelInitial}
                      onCancelConfirmed={onCancelConfirmed}
                      onSetWinner={onSetWinner}
                      onCancelLotteryEntry={onCancelLotteryEntry}
                      onToggleDisableGame={onToggleDisableGame}
                      lotteryEntries={lotteryEntries ? lotteryEntries[game.id] : undefined}
                      mode={mode}
                      isGameDisabled={disabledGameIds?.has(game.id) ?? false}
                    />
                  ))}
              </List>
            </Paper>
          );
        })
      )}
    </>
  );
};

export default GameList;
